/**
 * Cookie Consent Component for GDPR compliance
 * Handles user consent for analytics cookies
 */

class CookieConsent {
  constructor() {
    this.bannerVisible = false;
    this.init();
  }

  init() {
    // Check if consent is required
    const config = window.APP_CONFIG?.analytics?.cookieConsent;
    if (!config?.required) return;

    // Check if consent is already given or denied
    if (this.hasConsentDecision()) return;

    // Show consent banner after a brief delay
    setTimeout(() => {
      this.showConsentBanner();
    }, 1000);
  }

  showConsentBanner() {
    if (this.bannerVisible) return;

    const banner = document.createElement('div');
    banner.id = 'cookie-consent-banner';
    banner.setAttribute('role', 'dialog');
    banner.setAttribute('aria-label', 'Cookie Consent');
    
    banner.innerHTML = `
      <div class="cookie-consent-content">
        <div class="cookie-consent-text">
          <h4>🍪 We use cookies</h4>
          <p>We use cookies and similar technologies to analyze website traffic, enhance user experience, and provide personalized content. You can manage your preferences at any time.</p>
        </div>
        <div class="cookie-consent-buttons">
          <button id="accept-cookies" class="btn btn--primary" type="button">
            Accept All
          </button>
          <button id="decline-cookies" class="btn btn--secondary" type="button">
            Decline Analytics
          </button>
          <button id="cookie-settings" class="btn btn--link" type="button">
            Settings
          </button>
        </div>
      </div>
    `;

    // Add styles
    this.addStyles();
    
    document.body.appendChild(banner);
    this.bannerVisible = true;

    // Event listeners
    document.getElementById('accept-cookies').addEventListener('click', () => {
      this.grantConsent();
      this.hideBanner();
    });

    document.getElementById('decline-cookies').addEventListener('click', () => {
      this.denyConsent();
      this.hideBanner();
    });

    document.getElementById('cookie-settings').addEventListener('click', () => {
      this.showDetailedSettings();
    });

    // Allow closing with Escape key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.bannerVisible) {
        this.denyConsent();
        this.hideBanner();
      }
    });
  }

  showDetailedSettings() {
    const banner = document.getElementById('cookie-consent-banner');
    if (!banner) return;

    banner.innerHTML = `
      <div class="cookie-consent-content cookie-consent-detailed">
        <div class="cookie-consent-header">
          <h4>🍪 Cookie Preferences</h4>
          <button id="close-settings" class="close-btn" type="button" aria-label="Close">&times;</button>
        </div>
        <div class="cookie-consent-text">
          <div class="cookie-category">
            <h5>Essential Cookies</h5>
            <p>These cookies are necessary for the website to function and cannot be disabled.</p>
            <label class="cookie-toggle">
              <input type="checkbox" checked disabled>
              <span class="toggle-slider"></span>
              Always Active
            </label>
          </div>
          <div class="cookie-category">
            <h5>Analytics Cookies</h5>
            <p>These cookies help us understand how visitors interact with our website by collecting and reporting information anonymously.</p>
            <label class="cookie-toggle">
              <input type="checkbox" id="analytics-toggle">
              <span class="toggle-slider"></span>
              Enable Analytics
            </label>
          </div>
        </div>
        <div class="cookie-consent-buttons">
          <button id="save-preferences" class="btn btn--primary" type="button">
            Save Preferences
          </button>
          <button id="accept-all-detailed" class="btn btn--secondary" type="button">
            Accept All
          </button>
        </div>
      </div>
    `;

    // Event listeners for detailed view
    document.getElementById('close-settings').addEventListener('click', () => {
      this.showConsentBanner();
    });

    document.getElementById('save-preferences').addEventListener('click', () => {
      const analyticsEnabled = document.getElementById('analytics-toggle').checked;
      if (analyticsEnabled) {
        this.grantConsent();
      } else {
        this.denyConsent();
      }
      this.hideBanner();
    });

    document.getElementById('accept-all-detailed').addEventListener('click', () => {
      this.grantConsent();
      this.hideBanner();
    });
  }

  grantConsent() {
    localStorage.setItem('cookie_consent', 'granted');
    localStorage.setItem('analytics_consent', 'granted');
    
    // Update analytics service
    if (window.analytics) {
      window.analytics.updateConsent(true);
    }

    this.showSuccessMessage('Analytics cookies enabled. Thank you!');
  }

  denyConsent() {
    localStorage.setItem('cookie_consent', 'denied');
    localStorage.setItem('analytics_consent', 'denied');
    
    // Update analytics service
    if (window.analytics) {
      window.analytics.updateConsent(false);
    }

    this.showSuccessMessage('Analytics cookies disabled. You can change this anytime in settings.');
  }

  hideBanner() {
    const banner = document.getElementById('cookie-consent-banner');
    if (banner) {
      banner.style.animation = 'slideDown 0.3s ease-out';
      setTimeout(() => {
        banner.remove();
        this.bannerVisible = false;
      }, 300);
    }
  }

  showSuccessMessage(message) {
    // Use existing toast system if available
    if (window.showToast) {
      window.showToast(message, 'success');
    } else {
      console.log('[Cookie Consent]', message);
    }
  }

  hasConsentDecision() {
    return localStorage.getItem('cookie_consent') !== null;
  }

  hasConsent() {
    return localStorage.getItem('analytics_consent') === 'granted';
  }

  addStyles() {
    // Check if styles already added
    if (document.getElementById('cookie-consent-styles')) return;

    const styles = document.createElement('style');
    styles.id = 'cookie-consent-styles';
    styles.textContent = `
      #cookie-consent-banner {
        position: fixed;
        bottom: 0;
        left: 0;
        right: 0;
        background: linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%);
        color: white;
        padding: 1.5rem;
        z-index: 10000;
        box-shadow: 0 -4px 20px rgba(0, 0, 0, 0.3);
        animation: slideUp 0.3s ease-out;
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      }

      .cookie-consent-content {
        max-width: 1200px;
        margin: 0 auto;
        display: flex;
        align-items: center;
        gap: 2rem;
        flex-wrap: wrap;
      }

      .cookie-consent-content.cookie-consent-detailed {
        flex-direction: column;
        align-items: stretch;
        gap: 1.5rem;
      }

      .cookie-consent-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        width: 100%;
      }

      .cookie-consent-text {
        flex: 1;
        min-width: 300px;
      }

      .cookie-consent-text h4 {
        margin: 0 0 0.5rem 0;
        font-size: 1.2rem;
        font-weight: 600;
      }

      .cookie-consent-text p {
        margin: 0;
        font-size: 0.9rem;
        line-height: 1.4;
        opacity: 0.9;
      }

      .cookie-category {
        margin-bottom: 1.5rem;
        padding: 1rem;
        background: rgba(255, 255, 255, 0.05);
        border-radius: 8px;
      }

      .cookie-category h5 {
        margin: 0 0 0.5rem 0;
        font-size: 1rem;
        font-weight: 600;
        color: #4CAF50;
      }

      .cookie-toggle {
        display: flex;
        align-items: center;
        gap: 0.5rem;
        margin-top: 0.5rem;
        cursor: pointer;
      }

      .toggle-slider {
        position: relative;
        width: 50px;
        height: 24px;
        background: #ccc;
        border-radius: 12px;
        transition: background 0.3s;
      }

      .toggle-slider:before {
        content: '';
        position: absolute;
        width: 20px;
        height: 20px;
        border-radius: 50%;
        background: white;
        top: 2px;
        left: 2px;
        transition: transform 0.3s;
      }

      .cookie-toggle input:checked + .toggle-slider {
        background: #4CAF50;
      }

      .cookie-toggle input:checked + .toggle-slider:before {
        transform: translateX(26px);
      }

      .cookie-toggle input:disabled + .toggle-slider {
        background: #4CAF50;
        opacity: 0.7;
      }

      .cookie-consent-buttons {
        display: flex;
        gap: 1rem;
        flex-wrap: wrap;
      }

      .cookie-consent-buttons .btn {
        padding: 0.75rem 1.5rem;
        border: none;
        border-radius: 6px;
        font-size: 0.9rem;
        font-weight: 500;
        cursor: pointer;
        transition: all 0.3s ease;
        text-decoration: none;
        min-width: 120px;
      }

      .btn-primary {
        background: #4CAF50;
        color: white;
      }

      .btn-primary:hover {
        background: #45a049;
        transform: translateY(-1px);
      }

      .btn-secondary {
        background: transparent;
        color: #ccc;
        border: 1px solid #555;
      }

      .btn-secondary:hover {
        background: #555;
        color: white;
      }

      .btn-link {
        background: transparent;
        color: #4CAF50;
        text-decoration: underline;
        border: none;
        min-width: auto;
      }

      .btn-link:hover {
        color: #45a049;
      }

      .close-btn {
        background: none;
        border: none;
        color: #ccc;
        font-size: 1.5rem;
        cursor: pointer;
        padding: 0;
        width: 30px;
        height: 30px;
        display: flex;
        align-items: center;
        justify-content: center;
      }

      .close-btn:hover {
        color: white;
      }

      @keyframes slideUp {
        from {
          transform: translateY(100%);
        }
        to {
          transform: translateY(0);
        }
      }

      @keyframes slideDown {
        from {
          transform: translateY(0);
        }
        to {
          transform: translateY(100%);
        }
      }

      @media (max-width: 768px) {
        .cookie-consent-content {
          flex-direction: column;
          text-align: center;
          gap: 1rem;
        }

        .cookie-consent-buttons {
          justify-content: center;
          width: 100%;
        }

        .cookie-consent-buttons .btn {
          flex: 1;
          min-width: 100px;
        }
      }
    `;

    document.head.appendChild(styles);
  }

  // Public method to show consent banner again (for settings page)
  showSettings() {
    if (!this.bannerVisible) {
      this.showConsentBanner();
      setTimeout(() => {
        this.showDetailedSettings();
      }, 100);
    }
  }

  // Public method to revoke consent
  revokeConsent() {
    localStorage.removeItem('cookie_consent');
    localStorage.removeItem('analytics_consent');
    
    if (window.analytics) {
      window.analytics.updateConsent(false);
    }

    this.showSuccessMessage('Cookie preferences reset. Please refresh the page to see the consent banner again.');
  }
}

// Create singleton instance
const cookieConsent = new CookieConsent();

export default cookieConsent;
